import React, { useMemo, useState, useEffect } from "react";
import {
  Tabs,
  Tab,
  Button,
  IconButton,
  Popover,
  MenuItem,
  Modal,
} from "@mui/material";
import { DataGrid, GridMoreVertIcon } from "@mui/x-data-grid";
import { gridClasses } from "@mui/x-data-grid/constants";
import { TbFilterEdit } from "react-icons/tb";
import { TbAlertHexagonFilled } from "react-icons/tb";
import DateTimeModal from "../../Schedule/ScheduleListAdd/DateTimeModal";
import dayjs from "dayjs";
import { HttpService } from "../../../service/HttpService";
import FullScreenLoader from "../../../components/Loader/FullScreenLoader";
import PlantRankingModal from "../../../components/Schedule/fullSwap/PlantRankingModal";
import RoomLogMasterTable from "../../../components/RoomLogMaster/RoomLogMasterTable";
import { tr } from "date-fns/locale";
import { RxCross1 } from "react-icons/rx";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FaFileExcel } from "react-icons/fa";

const formatGrowthPhase = (val) => {
  if (!val) return "";
  if (val.toLowerCase().includes("week")) return val;
  const match = val.match(/^([a-zA-Z\s]+)\s*(\d+)$/);
  if (match) {
    return `${match[1].trim()} Week ${match[2]}`;
  }
  return val;
};

const FertigationHistory = ({
  roomId: externalRoomId,
  harvested = false,
  harvestedDate,
}) => {
  const [roomTab, setRoomTab] = useState(0);
  const [phaseTab, setPhaseTab] = useState(0);
  const [epicenterTab, setEpicenterTab] = useState(0);
  const [isPhaseTabManuallySelected, setIsPhaseTabManuallySelected] =
    useState(false);
  const [page, setPage] = useState(0);
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [growthPhases, setGrowthPhases] = useState([]);
  const [searchTop, setSearchTop] = useState("");
  const [searchBottom, setSearchBottom] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);
  const [plantRankingOpen, setPlantRankingOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [killedModalOpen, setKilledModalOpen] = useState(false);
  const [selectedKilledId, setSelectedKilledId] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [topTableRows, setTopTableRows] = useState([]);
  const [topTableColumns, setTopTableColumns] = useState([]);

  // Separate modal + date filter states for top and bottom tables
  const [openTopDateModal, setOpenTopDateModal] = useState(false);
  const [openBottomDateModal, setOpenBottomDateModal] = useState(false);

  const dateFilterTop = JSON.parse(
    localStorage.getItem("fertigationDateFilterTop"),
  );
  const dateFilterBottom = JSON.parse(
    localStorage.getItem("fertigationDateFilterBottom"),
  );

  const [startDateTop, setStartDateTop] = useState(dateFilterTop?.startDate);
  const [endDateTop, setEndDateTop] = useState(dateFilterTop?.endDate);
  const [selectedRadioTop, setSelectedRadioTop] = useState(
    dateFilterTop?.selectedRadio,
  );
  const [tempStartDateTop, setTempStartDateTop] = useState(
    dateFilterTop?.startDate,
  );
  const [tempEndDateTop, setTempEndDateTop] = useState(dateFilterTop?.endDate);
  const [tempSelectedRadioTop, setTempSelectedRadioTop] = useState(
    dateFilterTop?.selectedRadio,
  );

  const [startDateBottom, setStartDateBottom] = useState(
    dateFilterBottom?.startDate,
  );
  const [endDateBottom, setEndDateBottom] = useState(dateFilterBottom?.endDate);
  const [selectedRadioBottom, setSelectedRadioBottom] = useState(
    dateFilterBottom?.selectedRadio,
  );
  const [tempStartDateBottom, setTempStartDateBottom] = useState(
    dateFilterBottom?.startDate,
  );
  const [tempEndDateBottom, setTempEndDateBottom] = useState(
    dateFilterBottom?.endDate,
  );
  const [tempSelectedRadioBottom, setTempSelectedRadioBottom] = useState(
    dateFilterBottom?.selectedRadio,
  );
  const [filteredTopRows, setFilteredTopRows] = useState([]);

  // mapping of tab index → epicenter value from backend
  const tabTypes = ["E1", "E2", "E3"];

  // Fetch rooms for room tabs
  const fetchRooms = async () => {
    setRoomsLoading(true);
    try {
      const httpService = new HttpService();
      const res = await httpService.get("rooms?sortBy=name&sortOrder=ASC");

      const roomsData = res?.data?.data || [];
      // industry-standard: configurable special rooms
      const EXTRA_ROOMS = ["clone room", "mom room"];
      const filteredRooms = roomsData
        // .filter(
        //   (room) => room.name && /^room\s([1-9][0-9]?)$/i.test(room.name.trim())
        // )
        .filter((room) => {
          if (!room.name) return false;

          const name = room.name.trim().toLowerCase();

          // existing logic (unchanged)
          const isNumberedRoom = /^room\s([1-9][0-9]?)$/.test(name);

          // minimal addition
          const isExtraRoom = EXTRA_ROOMS.includes(name);

          return isNumberedRoom || isExtraRoom;
        })
        // .sort((a, b) => {
        //   const numA = parseInt(a.name.match(/\d+/)?.[0] || 0, 10);
        //   const numB = parseInt(b.name.match(/\d+/)?.[0] || 0, 10);
        //   return numA - numB;
        // });
        .sort((a, b) => {
          const getNumber = (name) => {
            const match = name.match(/\d+/);
            return match ? Number(match[0]) : Infinity;
          };

          return getNumber(a.name) - getNumber(b.name);
        });

      setRooms(filteredRooms);
      setRoomTab(0);
    } catch (e) {
      console.error("Error fetching rooms:", e);
      setRooms([]);
    }
    setRoomsLoading(false);
  };

  const handleKillPlant = async () => {
    try {
      const httpService = new HttpService();
      const currentGrowthPhaseId = growthPhases[phaseTab]?.id;
      await httpService.put(`mapping/killed-status/${selectedKilledId}`, {
        growth_phase_id: currentGrowthPhaseId,
      });
      toast.success("Plant killed successfully");
      setKilledModalOpen(false);
      setSelectedKilledId(null);
      fetchMappings();
    } catch (error) {
      toast.error("Failed to kill plant");
    }
  };

  const handlePlantRankingSuccess = async (rowId, overallGrowth) => {
    const httpService = new HttpService();
    const growthPhaseId = growthPhases[phaseTab]?.id;
    await httpService.put(`mapping/overall-growth/${rowId}`, {
      overall_growth: overallGrowth,
      growth_phase_id: growthPhaseId,
    });
    // Update local state after successful API call
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === rowId ? { ...row, overall_growth: overallGrowth } : row,
      ),
    );
    // Refresh the data to ensure consistency with server
    fetchMappings();
  };

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const httpService = new HttpService();
      const harvest_date = dayjs(harvestedDate).format("YYYY-MM-DD");
      let apiUrl;
      if (!harvested) {
        apiUrl = `mapping?search=${encodeURIComponent(searchBottom || "")}`;
      } else {
        apiUrl = `mapping/harvested-records?harvestDate=${harvest_date}&search=${encodeURIComponent(searchBottom || "")}`;
      }
      apiUrl += `&epicenter=${tabTypes[epicenterTab]}`;
      if (externalRoomId) {
        apiUrl += `&room_id=${externalRoomId}`;
      } else if (rooms.length && rooms[roomTab]) {
        apiUrl += `&room_id=${rooms[roomTab]?.id}`;
      }
      // if (growthPhases.length && growthPhases[phaseTab]) {
      //   apiUrl += `&growth_phase_id=${growthPhases[phaseTab]?.id}`;
      // }
      // if (harvested) {
      //   apiUrl += `&harvested=true`;
      // }
      if (
        startDateBottom !== undefined &&
        startDateBottom !== null &&
        endDateBottom !== undefined &&
        endDateBottom !== null &&
        startDateBottom !== "" &&
        endDateBottom !== ""
      ) {
        const formattedStart = dayjs(startDateBottom).format("MM-DD-YYYY");
        const formattedEnd = endDateBottom
          ? dayjs(endDateBottom).format("MM-DD-YYYY")
          : dayjs(startDateBottom).format("MM-DD-YYYY");
        apiUrl = `${apiUrl}&start_date=${formattedStart}&end_date=${formattedEnd}`;
      }

      const res = await httpService.get(apiUrl);
      const list = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data?.data)
          ? res.data?.data
          : [];
      const growthPhaseId = res?.data?.growthPhase?.growth_phase_id
        ? res?.data?.growthPhase?.growth_phase_id
        : 0;
      const pagination = res?.data?.pagination;
      const matchedPhase = growthPhases.find(
        (phase) => String(phase.id) === String(growthPhaseId),
      );

      const normalized = list.map((it, idx) => ({
        id: it.id ?? idx,
        metrc_id: it.matric_id || it.metrc_id || it.clone_id || "",
        bucket_id: it.bucket_id || it.bucket || "",
        planting_date: it.planting_date || it.date_of_planting || "",
        cultivar_name: it.cultivar_name || "",
        overall_growth: it.overall_growth || "-",
        killed_status: it.killed_status || false,
      }));

      if (matchedPhase && !isPhaseTabManuallySelected) {
        const phaseIndex = growthPhases.findIndex(
          (phase) => String(phase.id) === String(matchedPhase.id),
        );
        if (phaseIndex !== -1) {
          setPhaseTab(phaseIndex);
        }
      } else if (!matchedPhase && !isPhaseTabManuallySelected) {
        setPhaseTab(0);
      }
      setRows(normalized);
      setRowCount(pagination?.total);
    } catch (e) {
      setRows([]);
      setRowCount(0);
    }
    setLoading(false);
  };

  const fetchGrowthPhases = async () => {
    try {
      const httpService = new HttpService();
      const response = await httpService.get("growth-phase/get-growth-phase");
      setGrowthPhases(response.data || []);
    } catch (error) {
      console.error("Error fetching growth phases:", error);
      setGrowthPhases([]);
    }
  };

  const FIXED_ORDER = ["PPM (500 Scale)", "EC", "Target PH", "Solution Temp"];

  const fetchFertigationHistory = async () => {
    setLoading(true);
    setTopTableRows([]);
    try {
      const httpService = new HttpService();
      let apiUrl = "task/fertigation-history?";

      apiUrl += `epicenter=${tabTypes[epicenterTab] || ""}`;

      if (externalRoomId) {
        apiUrl += `&room_id=${externalRoomId}`;
      } else if (rooms.length && rooms[roomTab]) {
        apiUrl += `&room_id=${rooms[roomTab]?.id}`;
      }

      // if (growthPhases.length && growthPhases[phaseTab]) {
      //   apiUrl += `&growth_phase_id=${growthPhases[phaseTab]?.id}`;
      // }

      if (startDateTop && endDateTop) {
        const formattedStart = dayjs(startDateTop).format("MM-DD-YYYY");
        const formattedEnd = dayjs(endDateTop).format("MM-DD-YYYY");
        apiUrl += `&startDate=${formattedStart}&endDate=${formattedEnd}`;
      }

      const res = await httpService.get(apiUrl);
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : [];
      const baseColumns = [
        {
          field: "growth_phase",
          headerName: "Growth Phase",
          flex: 1,
          minWidth: 150,
        },
        {
          field: "fertigation_date",
          headerName: "Fertigation Date",
          flex: 1,
          minWidth: 200,
        },
        {
          field: "task_type",
          headerName: "Task Type",
          flex: 1,
          minWidth: 150,
        },
        {
          field: "recipe_name",
          headerName: "Recipe Name",
          flex: 1,
          minWidth: 150,
        },
      ];
      setTopTableColumns(baseColumns);
      if (list.length > 0) {
        const firstItem = list[0];
        const nutrientKeys = Object.keys(firstItem.nutrients || {});
        const addBacksKeys = Object.keys(firstItem.addbacks || {});

        const orderedAddBacksKeys = [
          ...FIXED_ORDER.filter((key) => addBacksKeys.includes(key)),
          ...addBacksKeys
            .filter((key) => !FIXED_ORDER.includes(key))
            .sort((a, b) => a.localeCompare(b)),
        ];

        const dynamicColumns = [
          ...baseColumns,
          ...orderedAddBacksKeys.map((key) => ({
            field: key.toLowerCase().replace(/\s+/g, "_").replace(/[()]/g, ""),
            headerName: key,
            flex: 1,
            minWidth: 150,
          })),
          ...nutrientKeys
            .slice() // safe copy (prevents mutation)
            .sort((a, b) => a.localeCompare(b))
            .map((key) => ({
              field: key
                .toLowerCase()
                .replace(/\s+/g, "_")
                .replace(/[()]/g, ""),
              headerName: key,
              flex: 1,
              minWidth: 150,
            })),
          { field: "emp_name", headerName: "EMP Name", flex: 1, minWidth: 160 },
        ];
        setTopTableColumns(dynamicColumns);
        // setTopTableColumns([
        //   ...dynamicColumns,
        //   ...dynamicAddBackColumns,
        //   ...dynamicNutrientColumns,
        //   { field: "emp_name", headerName: "EMP Name", flex: 1, minWidth: 160 },
        // ]);
      }

      const normalized = list.map((it, idx) => {
        const row = {
          id: `fertigation-${it.task_full_swap_id || idx}-${idx}`,
          fertigation_date: it.fertigation_date
            ? dayjs(it.fertigation_date).format("MM-DD-YYYY")
            : "",
          task_type: it.type || "",
          emp_name: it.emp_name || "",
          recipe_name: it.recipe_name || "",
          growth_phase: formatGrowthPhase(it.growth_phase || ""),
        };
        if (it.addbacks) {
          Object.keys(it.addbacks).forEach((key) => {
            const fieldName = key
              .toLowerCase()
              .replace(/\s+/g, "_")
              .replace(/[()]/g, "");
            const value = it.addbacks[key];
            row[fieldName] =
              value === null ||
              value === undefined ||
              value === 0 ||
              value === ""
                ? "-"
                : value;
          });
        }
        if (it.nutrients) {
          Object.keys(it.nutrients).forEach((key) => {
            const fieldName = key
              .toLowerCase()
              .replace(/\s+/g, "_")
              .replace(/[()]/g, "");
            const value = it.nutrients[key];
            row[fieldName] =
              value === null ||
              value === undefined ||
              value === 0 ||
              value === ""
                ? "-"
                : value;
          });
        }

        return row;
      });
      if (normalized.length > 0) {
        setTopTableRows(normalized);
      } else {
        setTopTableRows([]);
        setTopTableColumns(baseColumns);
      }
    } catch (e) {
      console.error("Error:", e);
      setTopTableRows([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    const dateFilterTop = JSON.parse(
      localStorage.getItem("fertigationDateFilterTop"),
    );
    const dateFilterBottom = JSON.parse(
      localStorage.getItem("fertigationDateFilterBottom"),
    );

    if (dateFilterTop && dateFilterTop?.selectedRadio) {
      setStartDateTop(dateFilterTop?.startDate);
      setEndDateTop(dateFilterTop?.endDate);
      setSelectedRadioTop(dateFilterTop?.selectedRadio);
    }

    if (dateFilterBottom && dateFilterBottom?.selectedRadio) {
      setStartDateBottom(dateFilterBottom?.startDate);
      setEndDateBottom(dateFilterBottom?.endDate);
      setSelectedRadioBottom(dateFilterBottom?.selectedRadio);
    }
  }, []);

  useEffect(() => {
    // if (!externalRoomId) {
    fetchRooms();
    // }
    fetchGrowthPhases();
  }, [externalRoomId]);

  useEffect(() => {
    if (externalRoomId || rooms.length > 0) {
      fetchMappings();
      fetchFertigationHistory();
    }
  }, [
    rooms,
    externalRoomId,
    epicenterTab,
    roomTab,
    phaseTab,
    searchBottom,
    startDateBottom,
    endDateBottom,
    startDateTop,
    endDateTop,
  ]);

  const getRoomName = () => {
    if (externalRoomId && rooms.length > 0) {
      const room = rooms.find(
        (r) =>
          r.id === externalRoomId || String(r.id) === String(externalRoomId),
      );
      if (room) return room.name;
    }
    return rooms[roomTab]?.name || "";
  };

  const handleExportExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Fertigation History");

      const exportColumns = topColumns.filter(
        (col) => col.field && col.field !== "action",
      );

      const headers = exportColumns.map((col) => col.headerName || col.field);
      worksheet.addRow(headers);

      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };

      filteredTopRows.forEach((row, idx) => {
        const rowData = exportColumns.map((col) => {
          let val = row[col.field];
          if (col.field === "growth_phase" && idx > 0) {
            const prevRow = filteredTopRows[idx - 1];
            if (prevRow && prevRow.growth_phase === row.growth_phase) {
              val = "";
            }
          }
          return val === null || val === undefined ? "" : val;
        });
        worksheet.addRow(rowData);
      });

      exportColumns.forEach((col, index) => {
        let maxLen = (col.headerName || col.field).toString().length;
        filteredTopRows.forEach((row) => {
          const val = row[col.field];
          if (val) {
            maxLen = Math.max(maxLen, val.toString().length);
          }
        });
        const column = worksheet.getColumn(index + 1);
        column.width = Math.max(maxLen + 4, 12);
      });

      const buffer = await workbook.xlsx.writeBuffer();

      const currentDate = dayjs().format("MM-DD-YYYY");
      const roomName = getRoomName();
      const fileName = roomName
        ? `${roomName.trim()} Fertigation with ${currentDate}.xlsx`
        : `Fertigation with ${currentDate}.xlsx`;

      saveAs(new Blob([buffer]), fileName);
      toast.success("Excel exported successfully!");
    } catch (error) {
      console.error("Error exporting excel:", error);
      toast.error("Failed to export Excel file");
    }
  };

  const epicenters = [
    "Epicenter 1",
    "Epicenter 2",
    "Epicenter 3",
    // "Room Log Master",
  ];

  const commonTabsSx = {
    backgroundColor: "#fffffc",
    "& .MuiTabs-indicator": {
      backgroundColor: "#00A272",
      height: "3px",
    },
    "& .MuiTab-root": {
      position: "relative",
      color: "#A1A2A1",
      fontWeight: 400,
      fontSize: "16px",
      textTransform: "none",
      padding: "12px 20px",
      marginRight: "8px",
      "&:not(.Mui-selected)::after": {
        content: '""',
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: "3px",
        backgroundColor: "#C4C4C4",
      },
      "&.Mui-selected": {
        color: "#00A272 !important",
        fontWeight: 600,
        backgroundColor: "#E6F6F1",
        borderRadius: "4px 4px 0 0",
      },
    },
  };

  const topRows = useMemo(() => topTableRows, [topTableRows, topTableColumns]);
  const topColumns = useMemo(() => {
    const cols =
      topTableColumns.length > 0
        ? topTableColumns
        : [
            {
              field: "growth_phase",
              headerName: "Growth Phase",
              flex: 1,
              minWidth: 150,
            },
            {
              field: "fertigation_date",
              headerName: "Fertigation Date",
              flex: 1,
              minWidth: 200,
            },
            {
              field: "task_type",
              headerName: "Task Type",
              flex: 1,
              minWidth: 150,
            },
            {
              field: "emp_name",
              headerName: "EMP Name",
              flex: 1,
              minWidth: 160,
            },
          ];

    return cols.map((col) => {
      if (col.field === "growth_phase") {
        return {
          ...col,
          align: "center",
          headerAlign: "center",
          renderCell: (params) => {
            const index = filteredTopRows.findIndex(
              (r) => r.id === params.row.id,
            );
            if (index > 0) {
              const prevRow = filteredTopRows[index - 1];
              if (
                prevRow &&
                prevRow.growth_phase === params.row.growth_phase
                // &&
                // (params.row.task_type === "Add Back" || params.row.task_type === prevRow.task_type)
              ) {
                return "";
              }
            }

            // Count how many subsequent rows merge with this one
            let N = 1;
            let i = index + 1;
            while (i < filteredTopRows.length) {
              const nextRow = filteredTopRows[i];
              const prevRow = filteredTopRows[i - 1];
              if (
                nextRow &&
                nextRow.growth_phase === params.row.growth_phase
                // &&
                // (nextRow.task_type === "Add Back" || nextRow.task_type === prevRow.task_type)
              ) {
                N++;
                i++;
              } else {
                break;
              }
            }

            if (N > 1) {
              const H = 53.5; // row height in pixels (including padding p: 1.8)
              const shift = ((N - 1) / 2) * H;
              return (
                <div
                  style={{
                    transform: `translateY(${shift}px)`,
                    zIndex: 10,
                    pointerEvents: "none",
                  }}
                >
                  {params.value}
                </div>
              );
            }

            return params.value;
          },
        };
      }
      return col;
    });
  }, [topTableColumns, filteredTopRows]);

  // API data for bottom table (plant details)
  const bottomRows = useMemo(() => rows, [rows]);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const bottomColumns = [
    ...(harvested
      ? []
      : [
          {
            field: "action",
            headerName: "Action",
            headerAlign: "center",
            align: "center",
            sortable: false,
            width: 160,
            renderCell: (params) => (
              <>
                {!params.row?.killed_status && (
                  <>
                    <IconButton
                      onClick={(e) => {
                        handleMenuClick(e);
                        setMenuRowId(params?.row?.id);
                      }}
                      sx={{ padding: 0 }}
                    >
                      <GridMoreVertIcon />
                    </IconButton>
                    {anchorEl && menuRowId === params?.row?.id && (
                      <Popover
                        className="popup-menu"
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={handleCloseMenu}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                      >
                        <MenuItem
                          onClick={() => {
                            setSelectedRowData(params?.row);
                            setPlantRankingOpen(true);
                            handleCloseMenu();
                          }}
                          className="menu-list-items"
                        >
                          <div className="menu-list-items-text">
                            Plant ranking
                          </div>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setSelectedKilledId(params?.row?.id);
                            setKilledModalOpen(true);
                            handleCloseMenu();
                          }}
                          className="menu-list-items"
                        >
                          <div className="menu-list-items-text">Killed</div>
                        </MenuItem>
                      </Popover>
                    )}
                  </>
                )}
              </>
            ),
          },
        ]),
    {
      field: "metrc_id",
      headerName: "Metric ID",
      flex: 1.3,
      minWidth: 230,
      renderCell: (params) => (
        <div>{params.row?.killed_status ? "-" : params.value}</div>
      ),
    },
    {
      field: "bucket_id",
      headerName: "Bucket ID",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "cultivar_name",
      headerName: "Cultivar Name",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <div>{params.row?.killed_status ? "-" : params.value}</div>
      ),
    },
    {
      field: "planting_date",
      headerName: "Planting Date",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <div>
          {params.row?.killed_status
            ? "-"
            : params.row?.planting_date
              ? dayjs(params.row.planting_date).format("MM-DD-YYYY")
              : "-"}
        </div>
      ),
    },
    {
      field: "overall_growth",
      headerName: "Overall Growth",
      flex: 1,
      minWidth: 190,
      renderCell: (params) => (
        <div>{params.row?.killed_status ? "-" : params.value}</div>
      ),
    },
  ];

  // Filter top rows based on search
  useEffect(() => {
    let filtered = [...topRows];

    if (searchTop) {
      filtered = filtered.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(searchTop.toLowerCase()),
        ),
      );
    }

    setFilteredTopRows(filtered);
  }, [topRows, searchTop]);

  const filteredBottomRows = bottomRows;

  return (
    <>
      {/* {(loading || roomsLoading) && <FullScreenLoader />} */}
      <div className="company">
        {!externalRoomId && (
          <div className="page-title">
            <div className="title-wrap">
              <h1>Fertigation History</h1>
            </div>
          </div>
        )}

        <div className="common_content">
          {!externalRoomId && (
            <Tabs
              value={roomTab}
              onChange={(e, newValue) => {
                setRoomTab(newValue);
                setPage(0);
                setIsPhaseTabManuallySelected(false);
              }}
              variant="scrollable"
              scrollButtons="false"
              sx={{
                backgroundColor: "#fffffc",
                ".MuiTabs-flexContainer": {
                  display: "flex",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#00A272",
                  height: "3px",
                },
                "& .MuiTab-root": {
                  position: "relative",
                  color: "#A1A2A1",
                  fontWeight: 400,
                  fontSize: "16px",
                  textTransform: "none",
                  padding: "12px 20px",
                  marginRight: "8px",
                  "&:not(.Mui-selected)::after": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "3px",
                    backgroundColor: "#C4C4C4",
                  },
                  "&.Mui-selected": {
                    color: "#00A272 !important",
                    fontWeight: 600,
                    backgroundColor: "#E6F6F1",
                    borderRadius: "4px 4px 0 0",
                  },
                },
                mb: 2,
              }}
            >
              {rooms.map((room) => (
                <Tab key={room.id} label={room.name} />
              ))}
            </Tabs>
          )}

          {/* Growth phase row commented out
          <Tabs
            value={phaseTab}
            onChange={(_, newValue) => {
              if (epicenterTab !== 3) {
                setPhaseTab(newValue);
                setIsPhaseTabManuallySelected(true);
              }
            }}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              ...(epicenterTab !== 3 ? commonTabsSx : {}),
              backgroundColor: "#fffffc",
              ".MuiTabs-flexContainer": {
                display: "flex",
                justifyContent: "flex-start",
              },
              mb: 2,
              ...(epicenterTab === 3 && {
                pointerEvents: "none",
                opacity: 0.5,
                "& .MuiTabs-indicator": {
                  display: "none",
                },
                "& .MuiTab-root": {
                  color: "#A1A2A1 !important",
                  cursor: "not-allowed",
                  fontWeight: 400,
                  fontSize: "16px",
                  textTransform: "none",
                  padding: "12px 20px",
                  marginRight: "8px",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "3px",
                    backgroundColor: "#C4C4C4",
                  },
                },
              }),
            }}
          >
            {growthPhases.map((phase) => (
              <Tab
                key={phase.id}
                label={phase.name}
                disabled={epicenterTab === 3}
              />
            ))}
          </Tabs>
          */}

          {/* Epicenter row */}
          <Tabs
            value={epicenterTab}
            onChange={(_, newValue) => setEpicenterTab(newValue)}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              ...commonTabsSx,
              ".MuiTabs-flexContainer": {
                display: "flex",
                justifyContent: "flex-start",
              },
              mb: 3,
            }}
          >
            {epicenters.map((epi) => (
              <Tab key={epi} label={epi} />
            ))}
          </Tabs>

          {/* Top table: Fertigation events */}
          {epicenterTab === 3 ? (
            rooms.length > 0 ? (
              <RoomLogMasterTable
                loading={loading}
                roomId={rooms[roomTab]?.id || rooms[0]?.id || 0}
              />
            ) : (
              <div style={{ textAlign: "center", padding: "20px" }}>
                Loading rooms...
              </div>
            )
          ) : (
            <>
              <div className="table-filter-wrap d-flex justify-content-between pb-0 pt-2">
                <div className="header_search-wrap">
                  <div className="header_search_bar common_content">
                    <div className="input-group input_group">
                      <button className="btn search-btn search_btn">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="15"
                          height="15"
                          viewBox="0 0 21 21"
                          fill="none"
                        >
                          <circle
                            cx="9.98856"
                            cy="9.98856"
                            r="8.98856"
                            stroke="#495057"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M16.2402 16.7071L19.7643 20.222"
                            stroke="#495057"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <input
                        type="search"
                        className="form-control form_control"
                        placeholder="Search..."
                        aria-label="Search fertigation"
                        onChange={(e) => setSearchTop(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <Button
                    variant="outlined"
                    color="primary"
                    className="filters-btn me-2"
                    style={{ textTransform: "none" }}
                    onClick={handleExportExcel}
                    startIcon={<FaFileExcel />}
                  >
                    Export
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    className="filters-btn"
                    style={{ textTransform: "none" }}
                    onClick={() => setOpenTopDateModal(true)}
                  >
                    <TbFilterEdit /> Filters
                  </Button>
                </div>

                {openTopDateModal && (
                  <DateTimeModal
                    handleDateTimeModal={() => setOpenTopDateModal(false)}
                    setStartDate={(e) => setTempStartDateTop(e)}
                    setEndDate={(e) => setTempEndDateTop(e)}
                    open={openTopDateModal}
                    fetchSchedule={() => {
                      setStartDateTop(tempStartDateTop);
                      setEndDateTop(tempEndDateTop);
                      setSelectedRadioTop(tempSelectedRadioTop);
                      const fertigationDateFilterTop = {
                        startDate: tempStartDateTop,
                        endDate: tempEndDateTop,
                        selectedRadio: tempSelectedRadioTop,
                      };
                      localStorage.setItem(
                        "fertigationDateFilterTop",
                        JSON.stringify(fertigationDateFilterTop),
                      );
                    }}
                    selectedRadio={tempSelectedRadioTop}
                    setSelectedRadio={(e) => setTempSelectedRadioTop(e)}
                    startDate={tempStartDateTop}
                    endDate={tempEndDateTop}
                  />
                )}
              </div>

              <div
                style={{
                  height: `${filteredTopRows?.length > 0 ? "" : "200px"}`,
                  width: "100%",
                  marginTop: 12,
                }}
              >
                <DataGrid
                  key={`top-grid-${roomTab}-${phaseTab}-${epicenterTab}`}
                  rows={filteredTopRows?.length > 0 ? filteredTopRows : []}
                  columns={topColumns}
                  getRowHeight={() => null}
                  getRowClassName={(params) =>
                    params.row.task_type === "Add Back" ? "add-back-row" : ""
                  }
                  getCellClassName={(params) => {
                    if (params.field === "growth_phase") {
                      const index = filteredTopRows.findIndex(
                        (r) => r.id === params.id,
                      );
                      const nextRow = filteredTopRows[index + 1];
                      if (
                        nextRow &&
                        nextRow.growth_phase === params.row.growth_phase
                        // &&
                        // (nextRow.task_type === "Add Back" || nextRow.task_type === params.row.task_type)
                      ) {
                        return "border-bottom-none";
                      }
                    }
                    return "";
                  }}
                  sx={{
                    border: "1px solid #E0E0E0",
                    [`& .${gridClasses.cell}`]: { p: 1.8 },
                    "& .add-back-row": {
                      backgroundColor: "#ffd3ac59 !important",
                      "&:hover": {
                        backgroundColor: "#ffd3ac70 !important",
                      },
                    },
                    "& .add-back-row .MuiDataGrid-cell[data-field='growth_phase']":
                      {
                        backgroundColor: "white !important",
                      },
                    "& .add-back-row:hover .MuiDataGrid-cell[data-field='growth_phase']":
                      {
                        backgroundColor: "white !important",
                      },
                    "& .MuiDataGrid-cell[data-field='growth_phase']": {
                      overflow: "visible !important",
                    },
                    "& .MuiDataGrid-row": {
                      overflow: "visible !important",
                    },
                    "& .border-bottom-none": {
                      borderBottom: "none !important",
                    },
                  }}
                  autoHeight={filteredTopRows.length > 0}
                  showCellVerticalBorder
                  showColumnVerticalBorder
                  pageSizeOptions={[10, 25, 50]}
                  hideFooter={filteredTopRows?.length === 0}
                />
              </div>
            </>
          )}

          {/* Bottom table: Plant details */}
          {/* {epicenterTab !== 3 && (
            <>
              <div className="table-filter-wrap d-flex justify-content-between pb-0 pt-2 mt-4">
                <div className="header_search-wrap">
                  <div className="header_search_bar common_content">
                    <div className="input-group input_group">
                      <button className="btn search-btn search_btn">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="10"
                          height="10"
                          viewBox="0 0 21 21"
                          fill="none"
                        >
                          <circle
                            cx="9.98856"
                            cy="9.98856"
                            r="8.98856"
                            stroke="#495057"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M16.2402 16.7071L19.7643 20.222"
                            stroke="#495057"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <input
                        type="search"
                        className="form-control form_control"
                        placeholder="Search..."
                        aria-label="Search plants"
                        onChange={(e) => setSearchBottom(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <Button
                  variant="outlined"
                  color="primary"
                  className="filters-btn"
                  style={{ textTransform: "none" }}
                  onClick={() => setOpenBottomDateModal(true)}
                >
                  <TbFilterEdit /> Filters
                </Button>

                {openBottomDateModal && (
                  <DateTimeModal
                    handleDateTimeModal={() => setOpenBottomDateModal(false)}
                    setStartDate={(e) => setTempStartDateBottom(e)}
                    setEndDate={(e) => setTempEndDateBottom(e)}
                    open={openBottomDateModal}
                    fetchSchedule={() => {
                      setStartDateBottom(tempStartDateBottom);
                      setEndDateBottom(tempEndDateBottom);
                      setSelectedRadioBottom(tempSelectedRadioBottom);
                      const fertigationDateFilterBottom = {
                        startDate: tempStartDateBottom,
                        endDate: tempEndDateBottom,
                        selectedRadio: tempSelectedRadioBottom,
                      };
                      localStorage.setItem(
                        "fertigationDateFilterBottom",
                        JSON.stringify(fertigationDateFilterBottom),
                      );
                    }}
                    selectedRadio={tempSelectedRadioBottom}
                    setSelectedRadio={(e) => setTempSelectedRadioBottom(e)}
                    startDate={tempStartDateBottom}
                    endDate={tempEndDateBottom}
                  />
                )}
              </div>

              <div
                style={{
                  height: `${filteredBottomRows?.length > 0 ? "" : "200px"}`,
                  width: "100%",
                  marginTop: 12,
                }}
              >
                <DataGrid
                  rows={filteredBottomRows}
                  columns={bottomColumns}
                  getRowHeight={() => "auto"}
                  sx={{
                    border: "1px solid #E0E0E0",
                    [`& .${gridClasses.cell}`]: { p: 1.8 },
                  }}
                  autoHeight
                  showCellVerticalBorder
                  showColumnVerticalBorder
                  pageSizeOptions={[10, 25, 50]}
                />
              </div>
            </>
          )} */}
        </div>
      </div>

      <PlantRankingModal
        open={plantRankingOpen}
        onClose={() => setPlantRankingOpen(false)}
        rowData={selectedRowData}
        onSuccess={handlePlantRankingSuccess}
      />

      <Modal
        open={killedModalOpen}
        onClose={() => setKilledModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div className="modal_block">
          <div className="modal_header">
            <div className="modal_title_wrapper">
              <h2 className="modal_title">Are you Sure?</h2>
            </div>
            <div className="cancel-btn-wrap">
              <RxCross1 onClick={() => setKilledModalOpen(false)} />
            </div>
          </div>
          <div className="modal_content">
            <div className="modal_content_inner">
              <div>
                <TbAlertHexagonFilled />
              </div>
              <div>
                <p>Are you sure you want to kill this plant?</p>
              </div>
            </div>
          </div>
          <div className="modal_footer">
            <button
              onClick={() => setKilledModalOpen(false)}
              className="btn btn-border"
            >
              No
            </button>
            <button onClick={handleKillPlant} className="btn btn-primary">
              Yes
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FertigationHistory;
